// Citizen Charter Studio — Super Admin control centre.
// Templates → Pages → Fields → Fixed PDF Sets → Colours/Fonts → Full Preview.
// Everything is data-driven: no code change is ever needed to restructure the charter.
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FontSelector from "@/components/charter/FontSelector";
import PaletteEditor from "@/components/charter/PaletteEditor";
import PdfPreviewDialog from "@/components/charter/PdfPreviewDialog";
import { charterPdfArtifacts } from "@/lib/charterPdf";
import {
  Plus, Pencil, Copy, Trash2, ArrowUp, ArrowDown, Loader2, Eye, Upload,
  BookOpen, User, Landmark, Users, LayoutGrid, ScrollText, FileStack, RefreshCw, PlayCircle,
} from "lucide-react";

const KIND_META = {
  cover: { label: "Front Cover", icon: BookOpen },
  note: { label: "Note / Message", icon: User },
  details: { label: "Details (label : value)", icon: Landmark },
  record_table: { label: "Repeating Table", icon: Users },
  blocks: { label: "Custom Blocks", icon: LayoutGrid },
  back_cover: { label: "Back Cover", icon: ScrollText },
  fixed_set: { label: "Fixed PDF Set", icon: FileStack },
};

const FIELD_TYPES = [
  { id: "text", label: "Text (single line)" },
  { id: "textarea", label: "Paragraph (multi-line)" },
  { id: "number", label: "Number" },
  { id: "photo", label: "Photo upload" },
  { id: "signature", label: "Signature upload" },
];

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

export default function CharterStudioTab() {
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [tpl, setTpl] = useState(null);
  const [fixedSets, setFixedSets] = useState([]);
  const [subtab, setSubtab] = useState("pages");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("Preview");
  const [busyPreview, setBusyPreview] = useState(false);

  const loadTemplates = (keep) => api.get("/charters/studio/templates").then(r => {
    setTemplates(r.data);
    if (!keep && r.data.length) setTplId(cur => cur || (r.data.find(t => t.status === "active") || r.data[0]).id);
  }).catch(e => toast.error(e?.response?.data?.detail || "Could not load templates"));
  const loadTpl = (id) => id && api.get(`/charters/studio/templates/${id}`).then(r => setTpl(r.data)).catch(() => setTpl(null));
  const loadSets = () => api.get("/charters/studio/fixed-sets").then(r => setFixedSets(r.data)).catch(() => {});

  useEffect(() => { loadTemplates(); loadSets(); }, []);
  useEffect(() => { loadTpl(tplId); }, [tplId]);

  const adopt = (fresh) => { setTpl(fresh); loadTemplates(true); };
  const err = (e) => toast.error(e?.response?.data?.detail || "Action failed");

  /* ---------- template actions ---------- */
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newYear, setNewYear] = useState("2026");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const createTemplate = async () => {
    try {
      const { data } = await api.post("/charters/studio/templates", { name: newName, year: parseInt(newYear) || 2026 });
      toast.success(`Template "${data.name}" created (draft)`);
      setNewOpen(false); setNewName("");
      await loadTemplates(true); setTplId(data.id);
    } catch (e) { err(e); }
  };

  /* ---------- page actions ---------- */
  const [addPageOpen, setAddPageOpen] = useState(false);
  const [pageForm, setPageForm] = useState({ kind: "record_table", title_ml: "", title_en: "", fixed_set_id: "" });
  const [editPage, setEditPage] = useState(null); // page object being renamed
  const [delPage, setDelPage] = useState(null);

  const movePage = async (i, dir) => {
    const pages = [...tpl.pages];
    const j = i + dir;
    if (j < 0 || j >= pages.length) return;
    [pages[i], pages[j]] = [pages[j], pages[i]];
    try { adopt((await api.post(`/charters/studio/templates/${tpl.id}/pages/reorder`, { page_ids: pages.map(p => p.id) })).data); }
    catch (e) { err(e); }
  };

  const togglePage = async (p, enabled) => {
    try { adopt((await api.patch(`/charters/studio/templates/${tpl.id}/pages/${p.id}`, { enabled })).data); }
    catch (e) { err(e); }
  };

  const addPage = async () => {
    try {
      const payload = { kind: pageForm.kind, title_ml: pageForm.title_ml, title_en: pageForm.title_en };
      if (pageForm.kind === "fixed_set") payload.config = { fixed_set_id: pageForm.fixed_set_id };
      adopt((await api.post(`/charters/studio/templates/${tpl.id}/pages`, payload)).data);
      setAddPageOpen(false);
      setPageForm({ kind: "record_table", title_ml: "", title_en: "", fixed_set_id: "" });
      toast.success("Page added");
    } catch (e) { err(e); }
  };

  /* ---------- fixed set actions ---------- */
  const setFileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [newSetName, setNewSetName] = useState("");
  const [newSetFile, setNewSetFile] = useState(null);
  const [busyUpload, setBusyUpload] = useState(false);

  const toDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const uploadSet = async () => {
    if (!newSetFile || !newSetName.trim()) { toast.error("Name the set and choose a PDF"); return; }
    setBusyUpload(true);
    try {
      const pdf_base64 = await toDataUrl(newSetFile);
      await api.post("/charters/studio/fixed-sets", { name: newSetName.trim(), pdf_base64 });
      toast.success("Fixed page set uploaded");
      setNewSetName(""); setNewSetFile(null);
      if (setFileRef.current) setFileRef.current.value = "";
      loadSets();
    } catch (e) { err(e); } finally { setBusyUpload(false); }
  };

  const replaceSet = async (file) => {
    if (!file || !replaceTarget) return;
    try {
      const pdf_base64 = await toDataUrl(file);
      await api.patch(`/charters/studio/fixed-sets/${replaceTarget}`, { pdf_base64 });
      toast.success("PDF replaced — byte-identical copy will appear in new downloads");
      setReplaceTarget(null);
      loadSets();
    } catch (e) { err(e); }
  };

  const previewSet = async (fs) => {
    const tid = toast.loading(`Loading ${fs.name}…`, { id: "fs-preview" });
    try {
      const { data } = await api.get(`/charters/fixed-sets/${fs.id}/file`);
      const raw = atob(data.pdf_base64.split(",", 1)[1]);
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      setPreviewTitle(`${fs.name} (${data.page_count} pages)`);
      setPreviewUrl(URL.createObjectURL(new Blob([bytes], { type: "application/pdf" })));
      toast.dismiss(tid);
    } catch (e) { err(e); }
  };

  /* ---------- studio full preview ---------- */
  const studioPreview = async () => {
    setBusyPreview(true);
    const tid = toast.loading("Building sample charter…", { id: "studio-preview" });
    try {
      const sample = buildSampleCharter(tpl);
      const ids = tpl.pages.filter(p => p.kind === "fixed_set" && p.enabled !== false).map(p => p.config?.fixed_set_id).filter(Boolean);
      const fixedSetsMap = {};
      await Promise.all(ids.map(async id => {
        try { fixedSetsMap[id] = (await api.get(`/charters/fixed-sets/${id}/file`)).data.pdf_base64; } catch { /* skip */ }
      }));
      const { blobUrl } = await charterPdfArtifacts(sample, { template: tpl, fixedSets: fixedSetsMap });
      setPreviewTitle(`Full Preview — ${tpl.name}`);
      setPreviewUrl(blobUrl);
      toast.success("Preview ready", { id: tid });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Preview failed", { id: tid });
    } finally { setBusyPreview(false); }
  };

  const tplBadge = (t) => t.status === "active" ? "bg-emerald-100 text-emerald-800" : t.status === "archived" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-800";

  return (
    <div className="space-y-4" data-testid="charter-studio">
      <Card className="p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-emerald-900/10 text-emerald-800 flex items-center justify-center shrink-0">
          <ScrollText className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-lg">Citizen Charter Studio</h3>
          <p className="text-sm text-muted-foreground">Complete control over the charter structure — templates, sections, fields, page order, fixed PDF sets, colours and fonts. No code changes needed.</p>
        </div>
      </Card>

      {/* template bar */}
      <Card className="p-4 flex flex-wrap items-center gap-2" data-testid="studio-template-bar">
        <Select value={tplId} onValueChange={setTplId}>
          <SelectTrigger className="w-72" data-testid="studio-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
          <SelectContent>
            {templates.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name} · {t.year} ({t.status})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {tpl && <Badge className={tplBadge(tpl)} data-testid="studio-template-status">{tpl.status.toUpperCase()}</Badge>}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} data-testid="studio-new-template"><Plus className="w-4 h-4 mr-1" /> New</Button>
        {tpl && (
          <>
            <Button size="sm" variant="outline" onClick={() => api.post(`/charters/studio/templates/${tpl.id}/duplicate`).then(r => { toast.success("Template duplicated"); loadTemplates(true); setTplId(r.data.id); }).catch(err)} data-testid="studio-duplicate-template"><Copy className="w-4 h-4 mr-1" /> Duplicate</Button>
            <Button size="sm" variant="outline" onClick={() => { setRenameVal(tpl.name); setRenameOpen(true); }} data-testid="studio-rename-template"><Pencil className="w-4 h-4 mr-1" /> Rename</Button>
            {tpl.status !== "active" ? (
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => api.post(`/charters/studio/templates/${tpl.id}/activate`).then(r => { adopt(r.data); toast.success("Template activated — members now use it"); }).catch(err)} data-testid="studio-activate-template">Activate</Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => api.post(`/charters/studio/templates/${tpl.id}/deactivate`).then(r => adopt(r.data)).catch(err)} data-testid="studio-deactivate-template">Deactivate</Button>
            )}
            {tpl.status !== "active" && (
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDeleteOpen(true)} data-testid="studio-delete-template"><Trash2 className="w-4 h-4" /></Button>
            )}
          </>
        )}
      </Card>

      {tpl && (
        <Tabs value={subtab} onValueChange={setSubtab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="pages" data-testid="studio-tab-pages">Pages & Order</TabsTrigger>
            <TabsTrigger value="fields" data-testid="studio-tab-fields">Sections & Fields</TabsTrigger>
            <TabsTrigger value="fixedsets" data-testid="studio-tab-fixedsets">Fixed PDF Sets</TabsTrigger>
            <TabsTrigger value="design" data-testid="studio-tab-design">Colours & Fonts</TabsTrigger>
            <TabsTrigger value="preview" data-testid="studio-tab-preview">Full Preview</TabsTrigger>
          </TabsList>

          {/* ---------------- Pages & Order ---------------- */}
          <TabsContent value="pages" className="mt-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold">Document sequence — the final PDF follows this exact order</div>
                <Button size="sm" onClick={() => setAddPageOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="studio-add-page"><Plus className="w-4 h-4 mr-1" /> Add Page</Button>
              </div>
              <ol className="space-y-2" data-testid="studio-page-list">
                {tpl.pages.map((p, i) => {
                  const Meta = KIND_META[p.kind] || KIND_META.blocks;
                  const Icon = Meta.icon;
                  const fsName = p.kind === "fixed_set" ? (fixedSets.find(f => f.id === p.config?.fixed_set_id)?.name || "⚠ missing set") : null;
                  return (
                    <li key={p.id} className={`flex items-center gap-2 rounded-lg border p-2.5 ${p.enabled === false ? "opacity-50 border-dashed" : "border-border"} bg-card`} data-testid={`studio-page-${p.id}`}>
                      <span className="w-7 h-7 rounded-full bg-emerald-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <Icon className="w-4 h-4 text-emerald-800 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>{p.title_ml || "—"}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{p.title_en} · {Meta.label}{fsName ? ` · ${fsName}` : ""}{p.kind === "fixed_set" ? " (byte-preserved)" : ""}</div>
                      </div>
                      <Switch checked={p.enabled !== false} onCheckedChange={v => togglePage(p, v)} data-testid={`studio-page-toggle-${p.id}`} />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => movePage(i, -1)} disabled={i === 0} data-testid={`studio-page-up-${p.id}`}><ArrowUp className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => movePage(i, 1)} disabled={i === tpl.pages.length - 1} data-testid={`studio-page-down-${p.id}`}><ArrowDown className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditPage({ ...p })} data-testid={`studio-page-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => api.post(`/charters/studio/templates/${tpl.id}/pages/${p.id}/duplicate`).then(r => { adopt(r.data); toast.success("Page duplicated"); }).catch(err)} data-testid={`studio-page-dup-${p.id}`}><Copy className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setDelPage(p)} data-testid={`studio-page-del-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </li>
                  );
                })}
              </ol>
            </Card>
          </TabsContent>

          {/* ---------------- Sections & Fields ---------------- */}
          <TabsContent value="fields" className="mt-4">
            <FieldsPanel tpl={tpl} adopt={adopt} err={err} />
          </TabsContent>

          {/* ---------------- Fixed PDF sets ---------------- */}
          <TabsContent value="fixedsets" className="mt-4">
            <Card className="p-4 space-y-4">
              <div className="rounded-lg border border-dashed border-emerald-700/40 bg-emerald-50/40 p-4" data-testid="studio-set-upload">
                <div className="text-sm font-semibold mb-2">Upload official fixed PDF page set</div>
                <p className="text-xs text-muted-foreground mb-3">These pages are copied into the final PDF byte-for-byte — never redrawn, recoloured or rasterized. Max ~10 MB per set.</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <Input placeholder="Set name (e.g. Official Pages 2026 · Part 1)" value={newSetName} onChange={e => setNewSetName(e.target.value)} className="w-72 bg-white" data-testid="studio-set-name" />
                  <Input ref={setFileRef} type="file" accept="application/pdf" onChange={e => setNewSetFile(e.target.files?.[0] || null)} className="w-64 bg-white" data-testid="studio-set-file" />
                  <Button size="sm" onClick={uploadSet} disabled={busyUpload} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="studio-set-upload-btn">
                    {busyUpload ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
                  </Button>
                </div>
              </div>
              <ul className="divide-y divide-border" data-testid="studio-set-list">
                {fixedSets.map(fs => (
                  <li key={fs.id} className="py-3 flex flex-wrap items-center gap-2" data-testid={`studio-set-${fs.id}`}>
                    <FileStack className="w-4 h-4 text-emerald-800 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{fs.name}</div>
                      <div className="text-[11px] text-muted-foreground">{fs.page_count} pages · uploaded {String(fs.created_at || "").slice(0, 10)}{fs.notes ? ` · ${fs.notes}` : ""}</div>
                    </div>
                    <Badge className={fs.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}>{fs.enabled ? "Enabled" : "Disabled"}</Badge>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => previewSet(fs)} data-testid={`studio-set-preview-${fs.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setReplaceTarget(fs.id); replaceRef.current?.click(); }} data-testid={`studio-set-replace-${fs.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Replace</Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => api.patch(`/charters/studio/fixed-sets/${fs.id}`, { enabled: !fs.enabled }).then(loadSets).catch(err)} data-testid={`studio-set-toggle-${fs.id}`}>{fs.enabled ? "Disable" : "Enable"}</Button>
                    <RenameInline name={fs.name} onSave={async (name) => { await api.patch(`/charters/studio/fixed-sets/${fs.id}`, { name }).then(loadSets).catch(err); }} testid={`studio-set-rename-${fs.id}`} />
                    <Button size="sm" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => api.delete(`/charters/studio/fixed-sets/${fs.id}`).then(() => { toast.success("Set deleted"); loadSets(); }).catch(err)} data-testid={`studio-set-del-${fs.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </li>
                ))}
                {!fixedSets.length && <li className="py-6 text-center text-sm text-muted-foreground">No fixed page sets yet — upload the official unchanged PDF pages.</li>}
              </ul>
              <input ref={replaceRef} type="file" accept="application/pdf" className="hidden" onChange={e => { replaceSet(e.target.files?.[0]); e.target.value = ""; }} data-testid="studio-set-replace-input" />
            </Card>
          </TabsContent>

          {/* ---------------- Colours & Fonts ---------------- */}
          <TabsContent value="design" className="mt-4">
            <DesignPanel tpl={tpl} adopt={adopt} err={err} />
          </TabsContent>

          {/* ---------------- Full preview ---------------- */}
          <TabsContent value="preview" className="mt-4">
            <Card className="p-8 text-center" data-testid="studio-preview-panel">
              <PlayCircle className="w-10 h-10 mx-auto text-emerald-800 mb-3" />
              <h4 className="font-bold">Full document preview</h4>
              <p className="text-sm text-muted-foreground mt-1 max-w-lg mx-auto">Generates the complete Citizen Charter with sample data — following this template's page order, colours and fonts, with fixed PDF sets merged byte-identical.</p>
              <Button className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={studioPreview} disabled={busyPreview} data-testid="studio-full-preview-btn">
                {busyPreview ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />} Generate Full Preview
              </Button>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* dialogs */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="studio-new-dialog">
          <DialogHeader><DialogTitle>New Charter Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Template name</Label><Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Citizen Charter 2027" data-testid="studio-new-name" /></div>
            <div><Label>Year</Label><Input type="number" value={newYear} onChange={e => setNewYear(e.target.value)} data-testid="studio-new-year" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createTemplate} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="studio-new-create">Create (draft)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent data-testid="studio-rename-dialog">
          <DialogHeader><DialogTitle>Rename Template</DialogTitle></DialogHeader>
          <Input value={renameVal} onChange={e => setRenameVal(e.target.value)} data-testid="studio-rename-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button onClick={() => api.patch(`/charters/studio/templates/${tpl.id}`, { name: renameVal }).then(r => { adopt(r.data); setRenameOpen(false); toast.success("Template renamed"); }).catch(err)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="studio-rename-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addPageOpen} onOpenChange={setAddPageOpen}>
        <DialogContent data-testid="studio-add-page-dialog">
          <DialogHeader><DialogTitle>Add Page</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Page type</Label>
              <Select value={pageForm.kind} onValueChange={v => setPageForm(f => ({ ...f, kind: v }))}>
                <SelectTrigger data-testid="studio-add-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {pageForm.kind === "fixed_set" && (
              <div>
                <Label>Fixed PDF set</Label>
                <Select value={pageForm.fixed_set_id} onValueChange={v => setPageForm(f => ({ ...f, fixed_set_id: v }))}>
                  <SelectTrigger data-testid="studio-add-fixedset"><SelectValue placeholder="Choose uploaded set" /></SelectTrigger>
                  <SelectContent>
                    {fixedSets.map(fs => <SelectItem key={fs.id} value={fs.id}>{fs.name} ({fs.page_count} pages)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Title (Malayalam)</Label><Input value={pageForm.title_ml} onChange={e => setPageForm(f => ({ ...f, title_ml: e.target.value }))} data-testid="studio-add-title-ml" /></div>
            <div><Label>Title (English)</Label><Input value={pageForm.title_en} onChange={e => setPageForm(f => ({ ...f, title_en: e.target.value }))} data-testid="studio-add-title-en" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPageOpen(false)}>Cancel</Button>
            <Button onClick={addPage} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="studio-add-page-save">Add Page</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* rename page */}
      <Dialog open={!!editPage} onOpenChange={v => !v && setEditPage(null)}>
        <DialogContent data-testid="studio-edit-page-dialog">
          <DialogHeader><DialogTitle>Edit Page — {(editPage && KIND_META[editPage.kind]?.label) || ""}</DialogTitle></DialogHeader>
          {editPage && (
            <div className="space-y-3">
              <div><Label>Title (Malayalam) — shown in form, navigation, preview and PDF</Label><Input value={editPage.title_ml || ""} onChange={e => setEditPage(p => ({ ...p, title_ml: e.target.value }))} data-testid="studio-edit-title-ml" /></div>
              <div><Label>Title (English)</Label><Input value={editPage.title_en || ""} onChange={e => setEditPage(p => ({ ...p, title_en: e.target.value }))} data-testid="studio-edit-title-en" /></div>
              {editPage.kind === "fixed_set" && (
                <div>
                  <Label>Fixed PDF set</Label>
                  <Select value={editPage.config?.fixed_set_id || ""} onValueChange={v => setEditPage(p => ({ ...p, config: { ...p.config, fixed_set_id: v } }))}>
                    <SelectTrigger data-testid="studio-edit-fixedset"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {fixedSets.map(fs => <SelectItem key={fs.id} value={fs.id}>{fs.name} ({fs.page_count} pages)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPage(null)}>Cancel</Button>
            <Button onClick={() => api.patch(`/charters/studio/templates/${tpl.id}/pages/${editPage.id}`, { title_ml: editPage.title_ml, title_en: editPage.title_en, config: editPage.config }).then(r => { adopt(r.data); setEditPage(null); toast.success("Page updated — new name appears everywhere"); }).catch(err)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="studio-edit-page-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delPage} onOpenChange={v => !v && setDelPage(null)}>
        <AlertDialogContent data-testid="studio-del-page-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete page “{delPage?.title_en}”?</AlertDialogTitle>
            <AlertDialogDescription>Members will no longer see this section. Their already-entered data is kept in the database but won't appear in new PDFs.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => api.delete(`/charters/studio/templates/${tpl.id}/pages/${delPage.id}`).then(r => { adopt(r.data); setDelPage(null); toast.success("Page deleted"); }).catch(err)} data-testid="studio-del-page-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template “{tpl?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. Active templates cannot be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => api.delete(`/charters/studio/templates/${tpl.id}`).then(() => { toast.success("Template deleted"); setDeleteOpen(false); setTpl(null); setTplId(""); loadTemplates(); }).catch(err)} data-testid="studio-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PdfPreviewDialog url={previewUrl} title={previewTitle} onClose={() => {
        if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ } }
        setPreviewUrl(null);
      }} />
    </div>
  );
}

/* ---------------- rename inline (fixed sets) ---------------- */
function RenameInline({ name, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(name);
  if (!editing) {
    return <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setVal(name); setEditing(true); }} data-testid={testid}><Pencil className="w-3.5 h-3.5 mr-1" /> Rename</Button>;
  }
  return (
    <span className="flex gap-1">
      <Input value={val} onChange={e => setVal(e.target.value)} className="h-8 w-44 text-xs" data-testid={`${testid}-input`} />
      <Button size="sm" className="h-8 text-xs bg-emerald-700 text-white" onClick={async () => { await onSave(val); setEditing(false); }} data-testid={`${testid}-save`}>Save</Button>
    </span>
  );
}

/* ---------------- fields panel ---------------- */
function FieldsPanel({ tpl, adopt, err }) {
  const dynamicPages = tpl.pages.filter(p => p.kind !== "fixed_set");
  const [pageId, setPageId] = useState("");
  const page = dynamicPages.find(p => p.id === pageId) || dynamicPages[0];
  const [draft, setDraft] = useState(null); // editable copy of page.config
  useEffect(() => { setDraft(page ? JSON.parse(JSON.stringify(page.config || {})) : null); }, [page?.id, tpl.updated_at]); // eslint-disable-line

  if (!page || !draft) return <Card className="p-6 text-sm text-muted-foreground">No editable pages.</Card>;
  const fields = draft.fields || [];

  const save = async () => {
    try {
      adopt((await api.patch(`/charters/studio/templates/${tpl.id}/pages/${page.id}`, { config: draft })).data);
      toast.success("Fields saved — the member form and PDF update immediately");
    } catch (e) { err(e); }
  };

  const setField = (i, k, v) => setDraft(d => ({ ...d, fields: d.fields.map((f, j) => (j === i ? { ...f, [k]: v } : f)) }));
  const moveField = (i, dir) => setDraft(d => {
    const arr = [...d.fields];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...d, fields: arr };
  });

  return (
    <Card className="p-4 space-y-4" data-testid="studio-fields-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs font-semibold">Section (page)</Label>
        <Select value={page.id} onValueChange={setPageId}>
          <SelectTrigger className="w-72" data-testid="studio-fields-page"><SelectValue /></SelectTrigger>
          <SelectContent>
            {dynamicPages.map(p => <SelectItem key={p.id} value={p.id}>{p.title_ml || p.title_en} · {(KIND_META[p.kind] || {}).label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} data-testid="studio-fields-save">Save Fields</Button>
      </div>

      {page.kind === "record_table" && (
        <div className="flex flex-wrap gap-6 rounded-md bg-muted/40 border border-border p-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!draft.photo} onCheckedChange={v => setDraft(d => ({ ...d, photo: v }))} data-testid="studio-fields-photo" /> Photo column</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!draft.count_generator} onCheckedChange={v => setDraft(d => ({ ...d, count_generator: v }))} data-testid="studio-fields-countgen" /> "Number of records" quick generator</label>
          {!!draft.count_generator && (
            <div className="w-full">
              <Label className="text-xs">Default designations (one per line, applied to generated rows)</Label>
              <Textarea rows={3} value={(draft.defaults || []).join("\n")} onChange={e => setDraft(d => ({ ...d, defaults: e.target.value.split("\n") }))} className="mt-1 bg-white" data-testid="studio-fields-defaults" />
            </div>
          )}
        </div>
      )}
      {page.kind === "blocks" && (
        <p className="text-sm text-muted-foreground rounded-md bg-muted/40 border border-border p-3">Custom-block pages are built by the member (headings, paragraphs, images, contact, signature, divider) — there are no fixed fields to manage here.</p>
      )}

      {page.kind !== "blocks" && (
        <>
          <div className="space-y-2" data-testid="studio-fields-list">
            {fields.map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border p-2 bg-card" data-testid={`studio-field-${i}`}>
                <Input value={f.label} onChange={e => setField(i, "label", e.target.value)} className="h-8 w-52 text-xs" placeholder="Field label" data-testid={`studio-field-label-${i}`} />
                <Input value={f.key} onChange={e => setField(i, "key", slug(e.target.value))} className="h-8 w-36 text-xs font-mono" placeholder="field_key" data-testid={`studio-field-key-${i}`} />
                <Select value={f.type} onValueChange={v => setField(i, "type", v)}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid={`studio-field-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex-1" />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveField(i, -1)} disabled={i === 0} data-testid={`studio-field-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} data-testid={`studio-field-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDraft(d => ({ ...d, fields: [...d.fields.slice(0, i + 1), { ...d.fields[i] }, ...d.fields.slice(i + 1)] }))} data-testid={`studio-field-dup-${i}`}><Copy className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => setDraft(d => ({ ...d, fields: d.fields.filter((_, j) => j !== i) }))} data-testid={`studio-field-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => setDraft(d => ({ ...d, fields: [...(d.fields || []), { key: `field_${(d.fields || []).length + 1}`, label: "New Field", type: "text", placeholder: "", enabled: true }] }))} data-testid="studio-field-add">
            <Plus className="w-4 h-4 mr-1" /> Add Field
          </Button>
          <p className="text-[11px] text-muted-foreground">Field keys (a-z 0-9 _) store the member's data — renaming a key orphans old data; rename labels freely.</p>
        </>
      )}
    </Card>
  );
}

/* ---------------- design panel (palette + fonts) ---------------- */
function DesignPanel({ tpl, adopt, err }) {
  const [palette, setPalette] = useState(tpl.palette);
  const [family, setFamily] = useState(tpl.fonts?.family || "Anek Malayalam");
  const [rpp, setRpp] = useState(tpl.design?.records_per_page || 8);
  useEffect(() => { setPalette(tpl.palette); setFamily(tpl.fonts?.family || "Anek Malayalam"); setRpp(tpl.design?.records_per_page || 8); }, [tpl.id]); // eslint-disable-line

  const save = async () => {
    try {
      adopt((await api.patch(`/charters/studio/templates/${tpl.id}`, { palette, fonts: { family }, design: { records_per_page: parseInt(rpp) || 8 } })).data);
      toast.success("Design defaults saved — NEW charters start from these; existing charters keep their own saved palette");
    } catch (e) { err(e); }
  };

  return (
    <Card className="p-4 space-y-5" data-testid="studio-design-panel">
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-72 space-y-1.5">
          <Label className="text-xs font-semibold">Default Malayalam font</Label>
          <FontSelector value={family} onChange={setFamily} testid="studio-font" />
        </div>
        <div className="w-44 space-y-1.5">
          <Label className="text-xs font-semibold">Records per table page</Label>
          <Input type="number" min="4" max="12" value={rpp} onChange={e => setRpp(e.target.value)} data-testid="studio-rpp" />
        </div>
        <div className="flex-1" />
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} data-testid="studio-design-save">Save Design Defaults</Button>
      </div>
      <div>
        <div className="text-sm font-semibold mb-1">Default colour palette</div>
        <p className="text-xs text-muted-foreground mb-3">Each Local Body's charter stores its own copy — changing these defaults never alters already-saved charters. Fixed PDF pages are never recoloured.</p>
        <PaletteEditor value={palette} onChange={setPalette} testid="studio-palette" />
      </div>
    </Card>
  );
}

/* ---------------- sample data for studio preview ---------------- */
function buildSampleCharter(tpl) {
  const sections = {};
  for (const p of tpl.pages) {
    if (p.kind === "fixed_set") continue;
    const fields = (p.config?.fields || []).filter(f => f.enabled !== false);
    if (p.kind === "record_table") {
      sections[p.section_key] = [0, 1, 2].map(i => {
        const row = {};
        fields.forEach(f => { row[f.key] = f.type === "number" ? String(i + 1) : `സാമ്പിൾ ${f.label} ${i + 1}`; });
        return row;
      });
    } else if (p.kind === "blocks") {
      sections[p.section_key] = [
        { id: "s1", type: "heading", text: "സാമ്പിൾ തലക്കെട്ട് · Sample Heading" },
        { id: "s2", type: "paragraph", text: "ഇത് ഒരു സാമ്പിൾ ഖണ്ഡികയാണ്. This preview follows the template's page order, palette and fonts." },
      ];
    } else {
      const obj = {};
      fields.forEach(f => {
        if (f.key === "year") obj[f.key] = String(tpl.year);
        else if (f.key === "title_ml") obj[f.key] = "പൗരാവകാശ രേഖ";
        else if (f.key === "subtitle_en") obj[f.key] = "CITIZEN CHARTER";
        else if (f.type === "textarea") obj[f.key] = `സാമ്പിൾ ഉള്ളടക്കം — ${f.label}. Sample content for preview.`;
        else if (f.type !== "photo" && f.type !== "signature") obj[f.key] = `സാമ്പിൾ ${f.label}`;
      });
      sections[p.section_key] = obj;
    }
  }
  return {
    year: tpl.year,
    local_body: { local_body_name: "സാമ്പിൾ പഞ്ചായത്ത്", local_body_type: "Grama Panchayat", district: "Thiruvananthapuram", block_panchayat: "Sample Block" },
    sections,
    palette: tpl.palette,
  };
}
